import { Box, Divider, Tooltip } from "@mui/material";
import { PersonAddAlt1Outlined } from "@mui/icons-material";
import grey from "@mui/material/colors/grey";
import { isEmpty, size } from "lodash";
import { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useUnderwritingHook } from "../../../hooks/useUnderwritingHook";
import { loanDrilldownSet } from "../../../redux/actions/loanDrilldownActions";
import { getUserFullNameFromEmail } from "../../../utils/functions/getUserFullNameFromEmail";
import RffForm from "../../finalForm/RffForm";
import RffDisplayField from "../../finalForm/inputs/RffDisplayField";
import RffSelectMultipleUserEmailField from "../../finalForm/inputs/RffSelectMultipleUserEmailField";
import RffGroup from "../../finalForm/shared/RffGroup";
import Flx from "../../layout/Flx";
import BasicModal from "../../modals/BasicModal";
import UserAvatar from "../../ui/UserAvatar";

// Max avatars shown before collapsing the rest into a "+N" chip.
const MAX_VISIBLE_AVATARS = 3;

// Saturated background palette (white text) so each avatar is color-coded by
// person — deterministic per name so the same user always gets the same color.
const AVATAR_COLORS = [
  "#7c3aed", // violet
  "#ea580c", // orange
  "#1e3a5f", // navy
  "#0891b2", // cyan
  "#db2777", // pink
  "#16a34a", // green
  "#ca8a04", // amber
  "#4f46e5", // indigo
];

const avatarColorForName = (name) => {
  const str = String(name || "");
  let hash = 0;
  for (let i = 0; i < str.length; i += 1) {
    hash = (hash * 31 + str.charCodeAt(i)) | 0;
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
};

const LoanPermittedUsersAvatarStack = ({
  label = "Permitted Users",
  onAddTooltip = "Manage access",
}) => {
  const loanDrilldown = useSelector((state) => state.loanDrilldown);
  const { updateLoanPermittedUsers, loading } = useUnderwritingHook();
  const dispatch = useDispatch();
  const users = useSelector((state) => state.users);
  const [show, setShow] = useState(false);

  const permittedUsersNamesString = useMemo(() => {
    const permittedUsers = loanDrilldown?.permittedUsers;
    if (isEmpty(permittedUsers)) {
      return "None";
    }

    const permittedUsersFullnames = permittedUsers.map((user) => {
      return getUserFullNameFromEmail(users, user);
    });

    return permittedUsersFullnames.join(", ");
  }, [loanDrilldown?.permittedUsers, users]);

  const permittedUsersFullNames = useMemo(() => {
    const permittedUsers = loanDrilldown?.permittedUsers;
    if (isEmpty(permittedUsers)) {
      return [];
    }
    return permittedUsers.map((user) => {
      return getUserFullNameFromEmail(users, user);
    });
  }, [loanDrilldown?.permittedUsers, users]);

  const initialValues = useMemo(() => {
    return {
      permittedUsers: loanDrilldown?.permittedUsers || [],
    };
  }, [loanDrilldown?.permittedUsers]);

  const onSubmit = (values) => {
    const updatedPermittedUsers = values?.permittedUsers;
    updateLoanPermittedUsers({
      loanId: loanDrilldown?._id,
      permittedUsers: updatedPermittedUsers,
      onSuccessFn: (res) => {
        dispatch(loanDrilldownSet(res));
        setShow(false);
      },
    });
  };

  const visibleUsers = permittedUsersFullNames.slice(0, MAX_VISIBLE_AVATARS);
  const overflowCount = Math.max(
    0,
    permittedUsersFullNames.length - MAX_VISIBLE_AVATARS,
  );

  return (
    <>
      {/* Overlapping color-coded avatars (capped, with a "+N" overflow chip)
          followed by the circular add-person button that opens the manage-access
          modal. */}
      <Flx ac gap={1} className="permitted-users-manage">
        {label ? (
          <Box sx={{ fontSize: "13px", color: "#33475b" }}>{label}</Box>
        ) : null}
        <Flx ac>
          {visibleUsers.map((name, i) => (
            <UserAvatar
              key={`${name}-${i}`}
              size={24}
              showTooltip
              label={name}
              sx={{
                border: "1.5px solid #ffffff",
                marginLeft: i === 0 ? 0 : -0.7,
                background: avatarColorForName(name),
                color: "#ffffff",
                fontWeight: 600,
              }}
            />
          ))}
          {overflowCount > 0 && (
            <UserAvatar
              size={24}
              sx={{
                marginLeft: -0.7,
                border: "1.5px solid #ffffff",
                background: grey[200],
                color: grey[700],
                fontSize: 10,
                fontWeight: 600,
              }}
            >
              +{overflowCount}
            </UserAvatar>
          )}
          <Tooltip title={onAddTooltip || "Manage access"} arrow>
            <Box
              onClick={() => setShow(true)}
              sx={{ cursor: "pointer", display: "inline-flex" }}
            >
              <UserAvatar
                size={24}
                sx={{
                  marginLeft: isEmpty(visibleUsers) ? 0 : -0.7,
                  border: `1px solid ${grey[600]}`,
                  background: "#ffffff",
                  color: grey[700],
                }}
              >
                <PersonAddAlt1Outlined />
              </UserAvatar>
            </Box>
          </Tooltip>
        </Flx>
      </Flx>

      <BasicModal
        show={show}
        onClose={() => setShow(false)}
        title="Permitted Users"
        maxWidth="md"
      >
        <Flx column gap={1} sx={{ mt: 1, mb: 3 }}>
          <RffDisplayField
            label={`Current Permitted Users (${size(loanDrilldown?.permittedUsers)})`}
            value={permittedUsersNamesString}
          />
        </Flx>
        <Divider sx={{ mb: 3 }} />
        <RffForm
          loading={loading}
          onSubmit={onSubmit}
          submitText="Add User"
          initialValues={initialValues}
        >
          <RffGroup>
            <RffSelectMultipleUserEmailField
              name="permittedUsers"
              displayFullLabel
              label="Select user to add to loan"
            />
          </RffGroup>
        </RffForm>
      </BasicModal>
    </>
  );
};

export default LoanPermittedUsersAvatarStack;
